import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { exportBookingsToExcel } from '../utils/exportExcel';
import { PAYMENT_MODES, CRUISE_COLOR_PALETTE } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { getBookingReferences } from '../utils/bookingReference';
import { downloadTicket } from '../utils/ticketPDF';
import RefundModal from './RefundModal';
import InvoiceModal from './InvoiceModal';
import InvoicePrint from './InvoicePrint';
import CreditNoteModal from './CreditNoteModal';
import SendTicketModal from './SendTicketModal';
import GuestListEditor from './GuestListEditor';
import { Anchor, Receipt, Ticket, Search, Ship, AlertTriangle, MessageCircle, Users, ChevronRight, Phone } from 'lucide-react';
import { eventsOnDay, typeLabel, STATUSES } from './Events';
import { fmt12 } from './TimeField';

const DECK_OPTIONS = ['Upper Deck', 'Middle Deck'];

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const toInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const [d, m, y] = ddmmyyyy.split('-');
  return `${y}-${m}-${d}`;
};
const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};
const formatDateWords = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m - 1]} ${y}`;
};

const arrowBtn = {
  width: 44, height: 44,
  background: '#0A1628',
  color: '#ffffff',
  border: 'none',
  borderRadius: 10,
  fontSize: '1.2rem',
  cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
  transition: 'background 0.15s',
};


// Explicit Asia/Kolkata — created_at is UTC, and a browser-local render would
// show the previous day for bookings made 00:00-05:29 IST on a non-IST machine.
const fmtBookedIST = (ts) => {
  if (!ts) return null;
  return new Date(ts).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

// "2 Adults + 1 Children + 1 TL" — shared by the desktop card and the phone card.
const paxLabel = (booking) => [
  `${booking.adults || 0} Adults`,
  booking.children > 0 ? `${booking.children} Children` : null,
  booking.tour_leaders > 0 ? `${booking.tour_leaders} TL` : null,
].filter(Boolean).join(' + ');

// ─── Shared sub-components ───────────────────────────────────────────────────

function DetailRow({ label, value }) {
  if (value == null || value === '' || value === 0) return null;
  return (
    <div className="detail-row">
      <span className="detail-row-label">{label}</span>
      <span className="detail-row-value">{value}</span>
    </div>
  );
}

function EventCard({ event, onEdit }) {
  const isConfirmed = event.status === 'confirmed' || event.status === 'completed';
  const accentColor = isConfirmed ? '#C9A84C' : '#f59e0b';
  const bgColor     = isConfirmed ? '#fffbf0' : '#fffdf0';
  const borderColor = isConfirmed ? '#C9A84C66' : '#f59e0b66';
  const tagBg       = isConfirmed ? '#C9A84C1a' : '#f59e0b1a';
  const tagColor    = isConfirmed ? '#7a5a10' : '#92400e';
  const tagBorder   = isConfirmed ? '#C9A84C66' : '#f59e0b66';
  const statusLabel = (STATUSES.find(st => st.value === event.status) || {}).label || event.status;
  const pax = event.pax_min == null && event.pax_max == null ? null
    : event.pax_min === event.pax_max || event.pax_max == null ? event.pax_min
    : event.pax_min == null ? event.pax_max : `${event.pax_min} to ${event.pax_max}`;
  const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
  const time = [fmt12(hhmm(event.start_time)), fmt12(hhmm(event.end_time))].filter(Boolean).join(' – ');

  return (
    <div className="charter-card" style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderLeft: `4px solid ${accentColor}`,
      borderRadius: 12,
      padding: '14px 16px',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <span style={{
            background: tagBg, color: tagColor, border: `1px solid ${tagBorder}`,
            borderRadius: 20, padding: '2px 10px',
            fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.5px',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}><Anchor size={12} strokeWidth={1.75} aria-hidden="true" /> {typeLabel(event.event_type).toUpperCase()}</span>
          <span style={{
            background: tagBg, color: tagColor, border: `1px solid ${tagBorder}`,
            borderRadius: 20, padding: '2px 10px',
            fontSize: '0.68rem', fontWeight: 600,
          }}>{statusLabel}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn btn-sm btn-outline" onClick={() => onEdit(event)}>Edit</button>
        </div>
      </div>

      <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0A1628', marginBottom: 8, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
        {event.title}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {(event.cruise_types || []).map((c) => (
          <span key={c} style={{ background: '#0A162210', border: '1px solid #0A162220', borderRadius: 4, padding: '2px 8px', fontSize: '0.73rem', color: '#2a3a5a', fontWeight: 500 }}>{c}</span>
        ))}
        {(event.decks || []).map((d) => (
          <span key={d} style={{ background: '#0A162210', border: '1px solid #0A162220', borderRadius: 4, padding: '2px 8px', fontSize: '0.73rem', color: '#2a3a5a', fontWeight: 500 }}>{d}</span>
        ))}
      </div>

      <DetailRow label="Partner" value={event.partner_name} />
      <DetailRow label="Contact" value={event.contact_name} />
      <DetailRow label="Phone"   value={event.contact_phone} />
      <DetailRow label="Pax"     value={pax} />
      <DetailRow label="Time"    value={time} />
      <DetailRow label="Notes"   value={event.notes} />
    </div>
  );
}

const DEFAULT_CC = { leftBorder: 'var(--color-border)', bg: '#ffffff', border: 'var(--color-border)' };

const similarNames = (a, b) => {
  const na = (a || '').toLowerCase().replace(/\s+/g, '');
  const nb = (b || '').toLowerCase().replace(/\s+/g, '');
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  const chars = new Set([...na]);
  const overlap = [...chars].filter(c => nb.includes(c)).length;
  return overlap / Math.max(na.length, nb.length) >= 0.8;
};

// variant: 'desktop' (default) — the always-on desktop card, hidden <=680px;
// 'sheet' — the same full card rendered inside the phone detail sheet.
function BookingCard({ booking, reference, onDownloadTicket, onSendWhatsApp, onEdit, onDelete, onRefund, onCreditNote, isDuplicate, onDupClick, onGenerateInvoice, invoice, variant = 'desktop' }) {
  const { settings: cruiseSettings } = useCruiseSettings();
  const idx = cruiseSettings.findIndex(s => s.cruise_name === booking.cruise_type);
  const cc = idx >= 0 ? CRUISE_COLOR_PALETTE[idx % CRUISE_COLOR_PALETTE.length] : DEFAULT_CC;
  const [showGuests, setShowGuests] = useState(false); // collapsed by default

  const paxStr = paxLabel(booking);

  return (
    <div className={`booking-card ${variant === 'sheet' ? 'bk-sheet-card' : 'bk-desktop'}`} style={{
      background: cc.bg,
      border: `1px solid ${cc.border}`,
      borderLeft: `4px solid ${cc.leftBorder}`,
      borderRadius: 12,
      padding: '14px 16px',
      marginBottom: 10,
      boxShadow: '0 1px 4px rgba(10,22,40,0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <span style={{ background: '#0A162210', border: '1px solid #0A162220', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#2a3a5a', fontWeight: 600 }}>
            {booking.cruise_type}
          </span>
          <span style={{ background: '#0A162210', border: '1px solid #0A162220', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#2a3a5a', fontWeight: 600 }}>
            {booking.deck}
          </span>
          {booking.payment_status && (
            <span className={`badge ${booking.payment_status === 'Confirmed' ? 'badge-green' : 'badge-amber'}`}>
              {booking.payment_status}
            </span>
          )}
          {booking.payment_mode && (
            <span style={{ background: '#C9A84C12', border: '1px solid #C9A84C44', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#7a5a10', fontWeight: 600 }}>
              {booking.payment_mode}
            </span>
          )}
          {booking.is_refunded && (
            <span style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#166534', fontWeight: 700 }}>
              ✓ Refunded
            </span>
          )}
          {invoice && (
            <span style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#1d4ed8', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Receipt size={13} strokeWidth={1.75} aria-hidden="true" /> {invoice.invoice_number}
            </span>
          )}
          {isDuplicate && (
            <button
              onClick={() => onDupClick && onDupClick(booking)}
              title="Possible duplicate — click to compare"
              style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: '2px 8px', fontSize: '0.71rem', color: '#c2410c', fontWeight: 700, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <AlertTriangle size={13} strokeWidth={1.75} aria-hidden="true" /> Duplicate?
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="btn btn-sm btn-outline" onClick={() => onEdit(booking)}>Edit</button>
          <button className="btn btn-sm btn-danger"  onClick={() => onDelete(booking)}>Delete</button>
          {invoice && (
            <button className="btn btn-sm btn-outline" onClick={() => onCreditNote && onCreditNote(booking)}>Credit Note</button>
          )}
          {booking.payment_status === 'Cancelled' && (
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onRefund && onRefund(booking)}
              style={{ borderColor: booking.is_refunded ? '#86efac' : 'var(--color-border-strong)', color: booking.is_refunded ? '#166534' : 'var(--color-ink-secondary)' }}
            >
              {booking.is_refunded ? 'Edit Refund' : 'Refund'}
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div className="bk-title-name" style={{ minWidth: 0, flex: variant === 'sheet' ? '1 1 100%' : 1 }}>
            <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0A1628', margin: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {booking.booking_name}
            </p>
            {reference && (
              <p style={{ margin: '3px 0 0', fontSize: '0.7rem', color: '#9a8a6a', fontWeight: 600, letterSpacing: '0.3px' }}>
                Ref: {reference}
              </p>
            )}
          </div>
          <button
            onClick={() => onDownloadTicket && onDownloadTicket(booking)}
            style={{ background: '#0A1628', color: '#C9A84C', border: 'none', borderRadius: 6, padding: '5px 11px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Ticket size={15} strokeWidth={1.75} aria-hidden="true" />Ticket
          </button>
          <button
            onClick={() => onSendWhatsApp && onSendWhatsApp(booking)}
            style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 11px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <MessageCircle size={15} strokeWidth={1.75} aria-hidden="true" />WhatsApp
          </button>
          <button
            onClick={() => onGenerateInvoice && onGenerateInvoice(booking)}
            style={{ background: invoice ? '#eff6ff' : 'var(--color-sunken)', color: invoice ? '#1d4ed8' : 'var(--color-ink-secondary)', border: `1px solid ${invoice ? '#93c5fd' : 'var(--color-border-strong)'}`, borderRadius: 6, padding: '5px 11px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Receipt size={15} strokeWidth={1.75} aria-hidden="true" />{invoice ? 'Invoice ✓' : 'Invoice'}
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <DetailRow label="Pax"       value={paxStr} />
        <DetailRow label="Partner"   value={booking.partner_name} />
        <DetailRow label="Guest"     value={booking.guest_phone} />
        <DetailRow label="Partner"   value={booking.partner_phone} />
        <DetailRow label="Total"     value={fmt(booking.total_amount)} />
        <DetailRow label="TAC"       value={booking.tac > 0 ? fmt(booking.tac) : null} />
        <DetailRow label="Net Total" value={fmt(booking.net_total)} />
        <DetailRow label="Payment"   value={booking.payment_mode} />
        <DetailRow label="Booked"    value={fmtBookedIST(booking.created_at)} />
        {booking.notes && (
          <div style={{ gridColumn: '1 / -1' }}>
            <DetailRow label="Notes" value={booking.notes} />
          </div>
        )}
      </div>

      {/* Collapsible Guest list panel — collapsed by default (not shown at a glance) */}
      <div style={{ marginTop: 10, borderTop: '1px solid #E4E7EC20', paddingTop: 8 }}>
        <button
          type="button"
          onClick={() => setShowGuests(v => !v)}
          aria-expanded={showGuests}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontSize: '0.78rem', fontWeight: 700, color: '#2a3a5a' }}
        >
          <ChevronRight size={15} strokeWidth={2} aria-hidden="true" style={{ transform: showGuests ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
          <Users size={15} strokeWidth={1.75} aria-hidden="true" />
          Guest list
        </button>
        {showGuests && (
          <div style={{ marginTop: 10, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, padding: '14px 16px' }}>
            <GuestListEditor bookingId={booking.id} primaryName={booking.booking_name} adults={booking.adults} children={booking.children} />
          </div>
        )}
      </div>

    </div>
  );
}

// Compact phone card (<=680px, chosen by CSS — .bk-phone). Shows only what
// earns its place on a phone: name, status, deck, pax, net total, a tappable
// phone number, and Ticket/WhatsApp. Tapping the card opens the detail sheet;
// the interactive elements inside stop propagation so they never trigger it.
function BookingCardPhone({ booking, onOpen, onDownloadTicket, onSendWhatsApp }) {
  const { settings: cruiseSettings } = useCruiseSettings();
  const idx = cruiseSettings.findIndex(s => s.cruise_name === booking.cruise_type);
  const cc = idx >= 0 ? CRUISE_COLOR_PALETTE[idx % CRUISE_COLOR_PALETTE.length] : DEFAULT_CC;

  return (
    <div
      className="booking-card bk-phone"
      role="button"
      tabIndex={0}
      aria-label={`Open booking details for ${booking.booking_name}`}
      onClick={() => onOpen(booking)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(booking); } }}
      style={{
        background: cc.bg,
        border: `1px solid ${cc.border}`,
        borderLeft: `4px solid ${cc.leftBorder}`,
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 10,
        boxShadow: '0 1px 4px rgba(10,22,40,0.05)',
        cursor: 'pointer',
      }}
    >
      <div className="bk-phone-head">
        <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0A1628', margin: 0, minWidth: 0, flex: 1, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
          {booking.booking_name}
        </p>
        {booking.payment_status && (
          <span className={`badge ${booking.payment_status === 'Confirmed' ? 'badge-green' : 'badge-amber'}`}>
            {booking.payment_status}
          </span>
        )}
      </div>
      <p className="bk-phone-meta" style={{ color: '#4a5a7a', fontWeight: 500 }}>
        {booking.deck} · {paxLabel(booking)} · <span style={{ color: '#0A1628', fontWeight: 700 }}>Net {fmt(booking.net_total)}</span>
      </p>
      <div className="bk-phone-actions">
        {booking.guest_phone ? (
          <a
            className="bk-phone-tel"
            href={`tel:${booking.guest_phone}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: '#0A1628' }}
          >
            <Phone size={14} strokeWidth={1.75} aria-hidden="true" />{booking.guest_phone}
          </a>
        ) : <span />}
        <span style={{ flex: 1 }} />
        <button
          onClick={(e) => { e.stopPropagation(); onDownloadTicket(booking); }}
          style={{ background: '#0A1628', color: '#C9A84C', border: 'none', borderRadius: 8, padding: '5px 13px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          <Ticket size={15} strokeWidth={1.75} aria-hidden="true" />Ticket
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSendWhatsApp(booking); }}
          style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 13px', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          <MessageCircle size={15} strokeWidth={1.75} aria-hidden="true" />WhatsApp
        </button>
      </div>
    </div>
  );
}

// ─── Summary ─────────────────────────────────────────────────────────────────

function SummarySection({ bookings }) {
  if (!bookings.length) return null;

  const paxMap = {};
  bookings.forEach((b) => {
    const key = `${b.cruise_type} — ${b.deck}`;
    paxMap[key] = (paxMap[key] || 0) + (b.adults || 0) + (b.children || 0);
  });

  const payMap = {};
  PAYMENT_MODES.forEach((m) => { payMap[m] = 0; });
  bookings.forEach((b) => {
    if (b.payment_mode && payMap[b.payment_mode] !== undefined)
      payMap[b.payment_mode] += Number(b.total_amount) || 0;
  });

  const tacMap = {};
  bookings.forEach((b) => {
    if (b.partner_name && b.tac > 0)
      tacMap[b.partner_name] = (tacMap[b.partner_name] || 0) + (b.tac || 0);
  });

  const totalCollection = bookings.reduce((s, b) => s + (Number(b.total_amount) || 0), 0);

  return (
    <div style={{
      background: '#ffffff', border: '1px solid var(--color-border)',
      borderRadius: 14, padding: '20px 24px',
      marginTop: 24, boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
    }}>
      <h3 style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 16 }}>
        Summary
      </h3>

      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Pax per Slot</p>
        {Object.entries(paxMap).sort().map(([key, count]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 5 }}>
            <span style={{ color: '#4a5a7a' }}>{key}</span>
            <span style={{ color: '#0A1628', fontWeight: 700 }}>{count}</span>
          </div>
        ))}
      </div>

      <hr className="divider" />

      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Collection by Payment Mode</p>
        {PAYMENT_MODES.filter((m) => payMap[m] > 0).map((m) => (
          <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 5 }}>
            <span style={{ color: '#4a5a7a' }}>{m}</span>
            <span style={{ color: '#15803d', fontWeight: 700 }}>{fmt(payMap[m])}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
          <span style={{ color: '#0A1628', fontWeight: 700 }}>Total Collection</span>
          <span style={{ color: '#C9A84C', fontWeight: 800, fontSize: '1rem' }}>{fmt(totalCollection)}</span>
        </div>
      </div>

      {Object.keys(tacMap).length > 0 && (
        <>
          <hr className="divider" />
          <div>
            <p style={{ fontSize: '0.71rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>TAC per Partner</p>
            {Object.entries(tacMap).map(([partner, amount]) => (
              <div key={partner} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 5 }}>
                <span style={{ color: '#4a5a7a' }}>{partner}</span>
                <span style={{ color: '#92400e', fontWeight: 700 }}>{fmt(amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

const selectStyle = {
  background: '#ffffff',
  border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8,
  padding: '8px 10px',
  color: '#0A1628',
  fontSize: '0.84rem',
  fontWeight: 500,
  outline: 'none',
  cursor: 'pointer',
  flex: '1 1 130px',
  minWidth: 0,
};

function FilterBar({ search, onSearch, filterCruise, onFilterCruise, filterDeck, onFilterDeck, onClear, isActive, cruiseTypes }) {
  return (
    <div style={{
      background: '#ffffff', border: '1px solid var(--color-border)',
      borderRadius: 14, padding: '16px', marginBottom: 16,
      boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
    }}>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <span style={{
          position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', color: 'var(--color-ink-faint)', pointerEvents: 'none',
        }}><Search size={16} strokeWidth={1.75} aria-hidden="true" /></span>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search by booking name or partner…"
          style={{
            width: '100%',
            background: 'var(--color-sunken)',
            border: '1.5px solid var(--color-border-strong)',
            borderRadius: 8,
            padding: '9px 12px 9px 34px',
            color: '#0A1628',
            fontSize: '0.88rem',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#C9A84C'; e.target.style.boxShadow = '0 0 0 3px #C9A84C22'; }}
          onBlur={(e)  => { e.target.style.borderColor = 'var(--color-border-strong)'; e.target.style.boxShadow = 'none'; }}
        />
        {search && (
          <button
            onClick={() => onSearch('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-ink-faint)', fontSize: '1rem', lineHeight: 1, padding: 2 }}
          >×</button>
        )}
      </div>

      {/* Dropdowns + clear */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterCruise} onChange={(e) => onFilterCruise(e.target.value)} style={selectStyle}>
          <option value="">All Cruises</option>
          {(cruiseTypes || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={filterDeck} onChange={(e) => onFilterDeck(e.target.value)} style={selectStyle}>
          <option value="">All Decks</option>
          {DECK_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        {isActive && (
          <button
            onClick={onClear}
            style={{
              background: 'none', border: '1.5px solid var(--color-border-strong)',
              borderRadius: 8, padding: '7px 13px',
              color: 'var(--color-ink-muted)', fontSize: '0.8rem', fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.color = '#7a5a10'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; e.currentTarget.style.color = 'var(--color-ink-muted)'; }}
          >
            ✕ Clear all
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ViewBookings({ date, onNavigate }) {
  const [inputDate, setInputDate] = useState(() => toInputDate(date) || new Date().toLocaleDateString('en-CA'));
  const displayDate = toDisplayDate(inputDate);
  const dateWords   = formatDateWords(inputDate);

  const [bookings, setBookings]     = useState([]);
  const [dayEvents, setDayEvents]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [references, setReferences] = useState({});
  const [refundTarget, setRefundTarget] = useState(null);
  const [sendTarget, setSendTarget]     = useState(null); // booking to send via WhatsApp
  const [invoiceMap, setInvoiceMap]     = useState({});
  const [invoiceModal, setInvoiceModal] = useState(null); // { booking, existingInvoice? }
  const [invoicePrint, setInvoicePrint] = useState(null); // invoiceData
  const [cnTarget, setCnTarget]         = useState(null); // { booking, invoice }
  const [detailId, setDetailId]         = useState(null); // phone detail sheet — booking id

  const { settings: cruiseSettings } = useCruiseSettings();
  const cruiseOrder = cruiseSettings.map(s => s.cruise_name);

  // Filter state
  const [search, setSearch]               = useState('');
  const [filterCruise, setFilterCruise]   = useState('');
  const [filterDeck, setFilterDeck]       = useState('');

  const isFilterActive = search !== '' || filterCruise !== '' || filterDeck !== '';

  const clearFilters = () => { setSearch(''); setFilterCruise(''); setFilterDeck(''); };

  const shiftDay = (n) => {
    const d = new Date(inputDate);
    d.setDate(d.getDate() + n);
    setInputDate(d.toISOString().slice(0, 10));
  };

  const fetchAll = useCallback(async () => {
    if (!displayDate) return;
    setLoading(true);
    const [bRes, cRes] = await Promise.all([
      supabase.from('bookings').select('*').eq('date', displayDate).order('created_at'),
      eventsOnDay(inputDate),
    ]);
    const bkgs = bRes.data || [];
    setBookings(bkgs);
    setDayEvents(cRes.data || []);
    setLoading(false);
    if (bkgs.length) {
      const ids = bkgs.map(b => b.id);
      getBookingReferences(ids).then(refs => setReferences(refs));
      supabase.from('invoices')
        .select('id, booking_id, invoice_number, invoice_date, customer_name, customer_phone')
        .in('booking_id', ids)
        .then(({ data: invData }) => {
          const map = {};
          (invData || []).forEach(i => { map[i.booking_id] = i; });
          setInvoiceMap(map);
        });
    } else {
      setInvoiceMap({});
    }
  }, [displayDate, inputDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Realtime sync: refresh when bookings change from any view ───────────────
  useEffect(() => {
    if (!displayDate) return;
    const ch = supabase
      .channel(`vb-bk-${displayDate}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `date=eq.${displayDate}` },
        () => { fetchAll(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [displayDate, fetchAll]);

  // Derived filtered list
  const filteredBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (filterCruise && b.cruise_type !== filterCruise) return false;
      if (filterDeck   && b.deck       !== filterDeck)   return false;
      if (q) {
        const nameMatch    = (b.booking_name  || '').toLowerCase().includes(q);
        const partnerMatch = (b.partner_name  || '').toLowerCase().includes(q);
        if (!nameMatch && !partnerMatch) return false;
      }
      return true;
    });
  }, [bookings, search, filterCruise, filterDeck]);

  const [dupCompareTarget, setDupCompareTarget] = useState(null); // { booking, duplicates: [] }

  const duplicateMap = useMemo(() => {
    const map = new Map();
    const groups = {};
    bookings.forEach(b => {
      if (b.payment_status === 'Cancelled') return;
      const key = `${b.cruise_type}|${b.deck}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });
    Object.values(groups).forEach(group => {
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          if (similarNames(group[i].booking_name, group[j].booking_name)) {
            if (!map.has(group[i].id)) map.set(group[i].id, []);
            map.get(group[i].id).push(group[j]);
            if (!map.has(group[j].id)) map.set(group[j].id, []);
            map.get(group[j].id).push(group[i]);
          }
        }
      }
    });
    return map;
  }, [bookings]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { type, item, invoice } = deleteTarget;
    // Invoiced bookings can't be hard-deleted — a credit note is the correct
    // reversal path. Guard here as well as in the UI (belt-and-suspenders).
    if (type === 'booking' && invoice) return;
    await supabase.from('bookings').delete().eq('id', item.id);
    setDeleteTarget(null);
    fetchAll();
  };

  // Group filtered bookings by cruise
  const grouped = cruiseOrder.reduce((acc, cruise) => {
    acc[cruise] = filteredBookings.filter((b) => b.cruise_type === cruise);
    return acc;
  }, {});
  // Include any bookings with a cruise type not in our settings list
  const unknownCruises = [...new Set(filteredBookings.filter(b => !grouped[b.cruise_type]).map(b => b.cruise_type))];
  unknownCruises.forEach(c => { grouped[c] = filteredBookings.filter(b => b.cruise_type === c); });
  const allCruiseOrder = [...cruiseOrder, ...unknownCruises];

  const handleDownloadTicket = async (b) => {
    try {
      await downloadTicket(b, references[b.id] || null);
    } catch (err) {
      console.error('Ticket download failed:', err);
    }
  };

  const hasFilteredResults = filteredBookings.length > 0;
  const totalFilteredPax   = filteredBookings.reduce((s, b) => s + (b.adults || 0) + (b.children || 0), 0);

  // Phone detail sheet target — derived by id so realtime refreshes flow into
  // the open sheet, and a deleted booking closes it (find → undefined → null).
  const detailBooking = detailId ? (bookings.find(b => b.id === detailId) || null) : null;

  // Shared prop set for the full booking card — used by both the desktop list
  // card and the sheet copy so the two can never drift.
  const cardProps = (b) => ({
    booking: b,
    reference: references[b.id],
    onDownloadTicket: handleDownloadTicket,
    onSendWhatsApp: (item) => setSendTarget(item),
    onEdit: (item) => onNavigate('edit-inquiry', displayDate, item),
    onDelete: (item) => setDeleteTarget({ type: 'booking', item, invoice: invoiceMap[item.id] }),
    onRefund: (item) => setRefundTarget(item),
    onCreditNote: (item) => setCnTarget({ booking: item, invoice: invoiceMap[item.id] }),
    isDuplicate: duplicateMap.has(b.id),
    onDupClick: (item) => setDupCompareTarget({ booking: item, duplicates: duplicateMap.get(item.id) || [] }),
    invoice: invoiceMap[b.id],
    onGenerateInvoice: (item) => setInvoiceModal({ booking: item, existingInvoice: invoiceMap[item.id] }),
  });

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('home', displayDate)}>← Back</button>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#0A1628' }}>Bookings</h2>
      </div>

      {/* Date navigation */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--color-border)',
        borderRadius: 14,
        padding: '18px 20px',
        marginBottom: 18,
        boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              style={arrowBtn}
              onClick={() => shiftDay(-1)}
              onMouseEnter={(e) => e.currentTarget.style.background = '#C9A84C'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0A1628'}
              aria-label="Previous day"
            >←</button>

            <input
              type="date"
              value={inputDate}
              onChange={(e) => setInputDate(e.target.value)}
              style={{
                background: 'var(--color-sunken)',
                border: '1.5px solid var(--color-border-strong)',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#0A1628',
                fontSize: '0.95rem',
                fontWeight: 600,
                outline: 'none',
                flex: 1,
                maxWidth: 200,
                colorScheme: 'light',
              }}
            />

            <button
              style={arrowBtn}
              onClick={() => shiftDay(1)}
              onMouseEnter={(e) => e.currentTarget.style.background = '#C9A84C'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0A1628'}
              aria-label="Next day"
            >→</button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => onNavigate('daily-tracker', displayDate)}
            >
              Daily Tracker
            </button>
            <button
              className="btn btn-navy btn-sm"
              onClick={() => onNavigate('seating-sheet', displayDate)}
            >
              Seating Sheet
            </button>
          </div>
        </div>

        {dateWords && (
          <p style={{
            marginTop: 10,
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#C9A84C',
            letterSpacing: '-0.3px',
          }}>
            {dateWords}
          </p>
        )}
      </div>

      {/* Quick add — visible without scrolling */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <button className="btn btn-gold" onClick={() => onNavigate('add-inquiry', displayDate)}>+ Add Booking</button>
        <button className="btn btn-navy" onClick={() => onNavigate('event-form', displayDate, { event_type: 'private_charter', event_date: inputDate, returnTo: 'view-bookings', returnDate: displayDate })}><Anchor size={16} strokeWidth={1.75} aria-hidden="true" />Add Charter</button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>Loading…</p>
      ) : (
        <>
          {/* Events on this day (charters, weddings, dry dock) — never filtered.
              Edit opens the event form and comes back here; events are never
              deleted, cancellation is a status set in the form. */}
          {dayEvents.length > 0 && (
            <section style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a5a10', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Anchor size={14} strokeWidth={1.75} aria-hidden="true" /> Events ({dayEvents.length})
              </h3>
              {dayEvents.map((ev) => (
                <EventCard
                  key={ev.id} event={ev}
                  onEdit={(item) => onNavigate('event-form', displayDate, { ...item, returnTo: 'view-bookings', returnDate: displayDate })}
                />
              ))}
            </section>
          )}

          {/* Filter bar — only show if there are any bookings */}
          {bookings.length > 0 && (
            <FilterBar
              search={search}           onSearch={setSearch}
              filterCruise={filterCruise} onFilterCruise={setFilterCruise}
              filterDeck={filterDeck}     onFilterDeck={setFilterDeck}
              onClear={clearFilters}      isActive={isFilterActive}
              cruiseTypes={cruiseOrder}
            />
          )}

          {/* Filtered result count when active */}
          {isFilterActive && bookings.length > 0 && (
            <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 14, fontWeight: 500 }}>
              {hasFilteredResults
                ? `${filteredBookings.length} booking${filteredBookings.length !== 1 ? 's' : ''} · ${totalFilteredPax} pax`
                : 'No results'}
            </p>
          )}

          {/* Regular bookings grouped by cruise */}
          {allCruiseOrder.map((cruise) => {
            const group = grouped[cruise];
            if (!group || !group.length) return null;
            return (
              <section key={cruise} style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4a5a7a', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12 }}>
                  {cruise} — {group.reduce((s, b) => s + (b.adults || 0) + (b.children || 0), 0)} pax
                </h3>
                {group.map((b) => (
                  <Fragment key={b.id}>
                    {/* Desktop card (hidden <=680px) and phone card (shown <=680px) — CSS chooses */}
                    <BookingCard {...cardProps(b)} />
                    <BookingCardPhone
                      booking={b}
                      onOpen={(item) => setDetailId(item.id)}
                      onDownloadTicket={handleDownloadTicket}
                      onSendWhatsApp={(item) => setSendTarget(item)}
                    />
                  </Fragment>
                ))}
              </section>
            );
          })}

          {/* Empty states */}
          {!bookings.length && !dayEvents.length && (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>
              <div style={{ marginBottom: 10 }}><Ship size={32} strokeWidth={1.75} aria-hidden="true" /></div>
              No bookings for {displayDate}
            </div>
          )}
          {bookings.length > 0 && !hasFilteredResults && isFilterActive && (
            <div style={{ textAlign: 'center', padding: '36px 24px', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>
              <div style={{ marginBottom: 8 }}><Search size={32} strokeWidth={1.75} aria-hidden="true" /></div>
              No bookings found matching your search
              <div style={{ marginTop: 14 }}>
                <button className="btn btn-outline btn-sm" onClick={clearFilters}>Clear filters</button>
              </div>
            </div>
          )}

          {/* Summary reflects filtered results */}
          <SummarySection bookings={filteredBookings} />

          {/* Export uses filtered results */}
          {filteredBookings.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <button className="btn btn-gold" onClick={() => exportBookingsToExcel(filteredBookings, displayDate)}>
                ↓ Export to Excel
              </button>
            </div>
          )}
        </>
      )}

      {/* Add buttons */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-border)' }}>
        <button className="btn btn-gold"  onClick={() => onNavigate('add-inquiry', displayDate)}>+ Add Booking</button>
        <button className="btn btn-navy"  onClick={() => onNavigate('event-form', displayDate, { event_type: 'private_charter', event_date: inputDate, returnTo: 'view-bookings', returnDate: displayDate })}><Anchor size={16} strokeWidth={1.75} aria-hidden="true" />Add Private Charter</button>
      </div>

      {/* Phone detail sheet — the FULL booking card (all badges, fields, guest
          list, and all seven actions) in a bottom sheet. Opened only from the
          phone card; sits at z 900 so every modal an action opens (delete,
          refund, invoice, credit note, WhatsApp, duplicate) stacks above it. */}
      {detailBooking && (
        <div
          className="bk-sheet-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setDetailId(null); }}
        >
          <div className="bk-sheet" role="dialog" aria-modal="true" aria-label={`Booking details for ${detailBooking.booking_name}`}>
            <div className="bk-sheet-head">
              <h2 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0A1628', margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {detailBooking.booking_name}
              </h2>
              <button className="bk-sheet-close" onClick={() => setDetailId(null)} aria-label="Close booking details">×</button>
            </div>
            <div className="bk-sheet-body">
              <BookingCard {...cardProps(detailBooking)} variant="sheet" />
            </div>
          </div>
        </div>
      )}

      {/* Duplicate compare modal */}
      {dupCompareTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setDupCompareTarget(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 680, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(10,22,40,0.22)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 800, color: '#0A1628', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={18} strokeWidth={1.75} aria-hidden="true" /> Possible Duplicate Bookings</h2>
              <button onClick={() => setDupCompareTarget(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--color-ink-muted)', padding: '0 4px', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '18px 22px' }}>
              <div className="dup-compare-grid" style={{ display: 'grid', gap: 14, '--dup-cols': Math.min(1 + dupCompareTarget.duplicates.length, 3) }}>
                {[dupCompareTarget.booking, ...dupCompareTarget.duplicates].map((b, idx) => (
                  <div key={b.id} style={{ border: `2px solid ${idx === 0 ? '#C9A84C' : 'var(--color-border)'}`, borderRadius: 12, padding: '14px 16px', background: idx === 0 ? '#fffbf0' : '#fff' }}>
                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: idx === 0 ? '#7a5a10' : 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                      {idx === 0 ? 'This Booking' : `Possible Duplicate ${dupCompareTarget.duplicates.length > 1 ? idx : ''}`}
                    </div>
                    {[
                      ['Name',    b.booking_name],
                      ['Date',    b.date],
                      ['Cruise',  b.cruise_type],
                      ['Deck',    b.deck],
                      ['Adults',  b.adults],
                      ['Children',b.children || 0],
                      ['Phone',   b.guest_phone || '—'],
                      ['Total',   b.total_amount ? `₹${Number(b.total_amount).toLocaleString('en-IN')}` : '—'],
                      ['Status',  b.payment_status || '—'],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--color-border-subtle)', gap: 8 }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>{label}</span>
                        <span style={{ fontSize: '0.78rem', color: '#0A1628', fontWeight: label === 'Name' ? 700 : 500, textAlign: 'right' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundTarget && (
        <RefundModal
          booking={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSaved={(updated) => {
            setBookings(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
            setRefundTarget(null);
          }}
        />
      )}

      {sendTarget && (
        <SendTicketModal
          booking={sendTarget}
          reference={references[sendTarget.id] || null}
          onClose={() => setSendTarget(null)}
        />
      )}

      {/* Invoice modal */}
      {invoiceModal && (
        <InvoiceModal
          booking={invoiceModal.booking}
          existingInvoice={invoiceModal.existingInvoice}
          onClose={() => setInvoiceModal(null)}
          onGenerated={(invoiceData) => {
            setInvoiceMap(prev => ({ ...prev, [invoiceModal.booking.id]: { id: invoiceData.id, booking_id: invoiceModal.booking.id, invoice_number: invoiceData.invoiceNumber, invoice_date: invoiceData.invoiceDate, customer_name: invoiceData.customerName, customer_phone: invoiceData.customerPhone } }));
            setInvoiceModal(null);
            setInvoicePrint(invoiceData);
          }}
        />
      )}

      {/* Invoice print preview */}
      {invoicePrint && (
        <InvoicePrint
          invoiceData={invoicePrint}
          onClose={() => setInvoicePrint(null)}
        />
      )}

      {/* Credit note modal (handles existing CNs, cap + print internally) */}
      {cnTarget && (
        <CreditNoteModal
          booking={cnTarget.booking}
          invoice={cnTarget.invoice}
          onClose={() => setCnTarget(null)}
          onSaved={() => { /* modal shows its own print view; nothing to refresh here */ }}
        />
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24, maxWidth: 360, width: '100%', boxShadow: '0 8px 32px rgba(10,22,40,0.18)' }}>
            {deleteTarget.type === 'booking' && deleteTarget.invoice ? (
              <>
                <p style={{ fontWeight: 700, marginBottom: 8, color: '#0A1628', fontSize: '1rem' }}>Deletion Not Allowed</p>
                <p style={{ fontSize: '0.85rem', color: '#6a7a8a', marginBottom: 20, lineHeight: 1.5 }}>
                  This booking has invoice <strong style={{ color: '#0A1628' }}>{deleteTarget.invoice.invoice_number}</strong>. Deletion isn't allowed for invoiced bookings — issue a credit note and mark the booking Cancelled instead.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn btn-navy"
                    onClick={() => { setCnTarget({ booking: deleteTarget.item, invoice: deleteTarget.invoice }); setDeleteTarget(null); }}
                  >
                    Issue Credit Note
                  </button>
                  <button className="btn btn-outline" onClick={() => setDeleteTarget(null)}>Close</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontWeight: 700, marginBottom: 8, color: '#0A1628', fontSize: '1rem' }}>Confirm Delete</p>
                <p style={{ fontSize: '0.85rem', color: '#6a7a8a', marginBottom: 20, lineHeight: 1.5 }}>
                  Delete booking for <strong style={{ color: '#0A1628' }}>{deleteTarget.item.booking_name}</strong>? This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-danger"  onClick={handleDelete}>Delete</button>
                  <button className="btn btn-outline" onClick={() => setDeleteTarget(null)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
